import { Theme, css } from '@emotion/react';

const topLevelWidth = (fixedWidth: number | undefined) => css`
  width: ${fixedWidth ? fixedWidth + 'px' : '100%'};
`;

const focusBorderSize = '2px';

/**
 * Applies styles to an image indicating it is focused.
 * Internet searches seem to indicate this layered-border effect can be
 * achieved using box-shadows, but I was having trouble getting that to
 * work right within the context of an ImageList and its items.
 *
 * So instead we use an ordinary border for the "outer" border of the image,
 * and padding for the "inner" border, using background-color styling to
 * get the inner border to be the desired color.
 *
 * This will shrink the image by 8px horizontally & vertically as the user
 * moves the focus around, per DJ this is okay, even desired.
 */
const focusedBorder = ({ colors, interactiveStates }: Theme) =>
  interactiveStates.focus(css`
    // outer border:
    border: ${focusBorderSize} solid ${colors.borderPrimaryBase};
    // inner border:
    padding: ${focusBorderSize};
    background-color: ${colors.borderContrast};
    outline: none;
  `);

export const imageListOnlyOne = (theme: Theme, fixedWidth: number | undefined) => [
  topLevelWidth(fixedWidth),
  // For the single-item case, we leave space for the focus indicator as a frame
  // rather than shrinking the image down a few pixels on focus. That doesn't
  // work for displaying full-sized images with anything other than a square
  // aspect ratio
  css`
    & img {
      display: block;
      width: 100%;
      // height is left proportional
    }
  `,
  focusedBorder(theme),
];

export const imageListContainer = (theme: Theme, fixedWidth: number | undefined) => [
  topLevelWidth(fixedWidth),
  css`
    & button {
      cursor: pointer;
      filter: brightness(100%);
      height: 100%;
      transition: all ${theme.timings.duration.short} ${theme.timings.timingFunction.easeInOut};
      width: 100%;
      & img {
        // Leaving the img display as inline here doesn't seem to have
        // a bad effect when rendered inside a MuiImageList, but it feels
        // consistent with the single-item case, and defensive, to make contained
        // img elements block-display here also.
        display: block;
        height: 100%;
        object-fit: cover;
        width: 100%;
      }
      ${theme.interactiveStates.hover(css`
        filter: brightness(80%);
        transform: scale(1.015);
      `)}
      ${theme.interactiveStates.active(css`
        filter: brightness(80%);
        transform: scale(1.015);
      `)}
      ${focusedBorder(theme)};
    }
  `,
];
